import { computeOpenStatus, type HoursSource, type OpenStatus } from "@/lib/opening-hours";
import { useEffect, useState } from "react";

const TONE: Record<OpenStatus["state"], string> = {
  open: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closing_soon: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  unknown: "bg-slate-50 text-slate-400 border-slate-200",
};

const DOT: Record<OpenStatus["state"], string> = {
  open: "bg-emerald-500",
  closing_soon: "bg-amber-500",
  closed: "bg-slate-400",
  unknown: "bg-slate-300",
};

/**
 * Live opening status. The server renders the status for the request time and
 * the pill re-computes every 60s so a page left open never shows stale state.
 */
export function useLiveStatus(source: HoursSource): OpenStatus {
  const [status, setStatus] = useState<OpenStatus>(() => computeOpenStatus(source));
  useEffect(() => {
    setStatus(computeOpenStatus(source));
    const id = setInterval(() => setStatus(computeOpenStatus(source)), 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(source)]);
  return status;
}

export function OpenStatusPill({
  source,
  className = "",
  size = "md",
}: {
  source: HoursSource;
  className?: string;
  size?: "sm" | "md";
}) {
  const status = useLiveStatus(source);
  const pad = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${pad} ${TONE[status.state]} ${className}`}
      style={{ textTransform: "none" }}
    >
      <span className={`size-1.5 rounded-full ${DOT[status.state]} ${status.state === "open" ? "animate-pulse" : ""}`} />
      {status.label}
    </span>
  );
}
